"use client";

import { CircleLoading } from "@layer/icons";
import { cn } from "@layer/utils/cn";
import { RadioGroup as RadioGroupPrimitive } from "radix-ui";
import type * as React from "react";
import { Label } from "../label";

function RadioGroup({
	className,
	...props
}: React.ComponentProps<typeof RadioGroupPrimitive.Root>) {
	return (
		<RadioGroupPrimitive.Root
			data-slot="radio-group"
			className={cn("grid gap-3", className)}
			{...props}
		/>
	);
}

function RadioGroupItem({
	className,
	...props
}: React.ComponentProps<typeof RadioGroupPrimitive.Item>) {
	return (
		<RadioGroupPrimitive.Item
			data-slot="radio-group-item"
			className={cn(
				"border-input text-primary focus-visible:border-ring focus-visible:ring-ring/50 aria-invalid:ring-destructive/20 dark:aria-invalid:ring-destructive/40 aria-invalid:border-destructive dark:bg-input/30 aspect-square size-4 shrink-0 rounded-full border shadow-xs transition-[color,box-shadow] outline-none focus-visible:ring-[3px] disabled:cursor-not-allowed disabled:opacity-50",
				className,
			)}
			{...props}
		>
			<RadioGroupPrimitive.Indicator
				data-slot="radio-group-indicator"
				className="relative flex items-center justify-center"
			>
				<CircleLoading className="fill-primary absolute top-1/2 left-1/2 size-2 -translate-x-1/2 -translate-y-1/2" />
			</RadioGroupPrimitive.Indicator>
		</RadioGroupPrimitive.Item>
	);
}

type RadioGroupFieldItemsContainerProps = React.PropsWithChildren<{
	className?: string;
}>;

const RadioGroupFieldItemsContainer = ({
	children,
	className,
}: RadioGroupFieldItemsContainerProps) => {
	return (
		<div className={cn("flex flex-col gap-2 space-x-6 ", className)}>
			{children}
		</div>
	);
};

type RadioGroupFieldItemProps = {
	value: string;
	id: string;
	label: string;
};

const RadioGroupFieldItem = ({
	value,
	id,
	label,
}: RadioGroupFieldItemProps) => {
	return (
		<div className="flex items-center space-x-2 w-full border rounded-lg p-3">
			<RadioGroupItem value={value} id={id} />
			<Label htmlFor={value}>{label}</Label>
		</div>
	);
};

export {
	RadioGroup,
	RadioGroupItem,
	RadioGroupFieldItemsContainer,
	RadioGroupFieldItem,
};
